/**
 * Stat-type display map — the single shared source of truth for turning a raw
 * stat code (e.g. "passing_yards", "soccer_assists") into a friendly display
 * name (e.g. "Passing Yards", "Assists").
 *
 * Consumed by BOTH the frontend live-stat display / over-under extraction
 * (apps/frontend/src/utils/pickem-helpers.ts) AND the admin question-preview
 * over/under adapter, so the two can never drift. Add new codes here only.
 */
export const statTypeDisplayMap: Record<string, string> = {
  // NFL stats
  rushing_yards: 'Rushing Yards',
  passing_yards: 'Passing Yards',
  receptions: 'Receptions',
  receiving_yards: 'Receiving Yards',
  touchdowns: 'Touchdowns',
  passing_touchdowns: 'Passing TDs',
  rushing_touchdowns: 'Rushing TDs',
  interceptions: 'Interceptions',
  fumbles: 'Fumbles',
  sacks: 'Sacks',
  tackles: 'Tackles',
  // NBA stats
  points: 'Points',
  assists: 'Assists',
  rebounds: 'Rebounds',
  steals: 'Steals',
  blocks: 'Blocks',
  blocked_att: 'Blocked Attempts',
  blocked_shots: 'Blocked Shots',
  turnovers: 'Turnovers',
  three_points_made: '3-Pointers Made',
  three_points_att: '3-Point Attempts',
  three_points_pct: '3-Point %',
  two_points_made: '2-Pointers Made',
  two_points_att: '2-Point Attempts',
  two_points_pct: '2-Point %',
  free_throws_made: 'Free Throws Made',
  free_throws_att: 'Free Throw Attempts',
  free_throws_pct: 'Free Throw %',
  field_goals_made: 'Field Goals Made',
  field_goals_att: 'Field Goal Attempts',
  field_goals_pct: 'Field Goal %',
  minutes: 'Minutes',
  offensive_rebounds: 'Offensive Rebounds',
  defensive_rebounds: 'Defensive Rebounds',
  personal_fouls: 'Personal Fouls',
  plus_minus: 'Plus/Minus',
  double_doubles: 'Double Doubles',
  triple_doubles: 'Triple Doubles',
  // Other sports
  goals: 'Goals',
  runs: 'Runs',
  hits: 'Hits',
  home_runs: 'Home Runs',
  rbi: 'RBIs',
  strikeouts: 'Strikeouts',
  // Soccer stats (codes are `soccer_`-prefixed; names from SOCCER_STAT_TYPES)
  soccer_goals: 'Goals',
  soccer_assists: 'Assists',
  soccer_shots: 'Shots',
  soccer_shots_on_target: 'Shots on Target',
  soccer_passes: 'Passes',
  soccer_pass_accuracy: 'Pass Accuracy',
  soccer_tackles: 'Tackles',
  soccer_clearances: 'Clearances',
  soccer_yellow_card: 'Yellow Cards',
  soccer_red_card: 'Red Cards',
  soccer_minutes_played: 'Minutes Played',
  soccer_saves: 'Saves',
  soccer_corners: 'Corners',
  soccer_fouls: 'Fouls',
  soccer_possession: 'Possession',
  soccer_team_goals: 'Team Goals',
  soccer_team_shots: 'Team Shots',
  soccer_team_shots_on_target: 'Team Shots on Target',
  soccer_team_yellow_cards: 'Team Yellow Cards',
  soccer_team_red_cards: 'Team Red Cards',
  // Tennis stats (codes are `tennis_`-prefixed; names from TENNIS_STAT_TYPES)
  tennis_aces: 'Aces',
  tennis_double_faults: 'Double Faults',
  tennis_breakpoints_won: 'Break Points Won',
  tennis_games_won: 'Games Won',
  tennis_sets_won: 'Sets Won',
  tennis_tiebreaks_won: 'Tiebreaks Won',
  tennis_service_games_won: 'Service Games Won',
  tennis_points_won: 'Total Points Won',
  tennis_first_serve_points_won: 'First Serve Points Won',
  tennis_second_serve_points_won: 'Second Serve Points Won',
  tennis_set_1_won: '1st Set Winner',
  tennis_set_2_won: '2nd Set Winner',
  tennis_set_3_won: '3rd Set Winner',
  tennis_set_4_won: '4th Set Winner',
  tennis_set_5_won: '5th Set Winner',
  tennis_match_tiebreak: 'Tie-break in the Match',
  // MMA stats (codes are `mma_`-prefixed; names from MMA_STAT_TYPES)
  mma_significant_strikes: 'Significant Strikes',
  mma_significant_strikes_attempted: 'Significant Strikes Attempted',
  mma_total_strikes: 'Total Strikes',
  mma_total_strikes_attempted: 'Total Strikes Attempted',
  mma_takedowns: 'Takedowns',
  mma_takedowns_attempted: 'Takedowns Attempted',
  mma_knockdowns: 'Knockdowns',
  mma_submission_attempts: 'Submission Attempts',
  mma_rounds_fought: 'Rounds Fought',
  // NASCAR stats (codes are `nascar_`-prefixed; names from NASCAR_STAT_TYPES)
  nascar_finish_position: 'Finishing Position',
  nascar_start_position: 'Starting Position',
  nascar_laps_led: 'Laps Led',
  nascar_times_led: 'Times Led',
  nascar_fastest_laps: 'Fastest Laps',
  nascar_laps_completed: 'Laps Completed',
  nascar_points: 'Race Points',
  nascar_stage_points: 'Stage Points',
  nascar_driver_rating: 'Driver Rating',
  nascar_avg_position: 'Average Running Position',
  nascar_pit_stops: 'Pit Stops',
};

/**
 * Resolve a raw stat code to its friendly display name, falling back to the
 * raw code when unmapped (so a new/unknown code still renders something rather
 * than blank). Identical semantics to the previous inline
 * `statTypeDisplayMap[code] || code` expression.
 */
export function getStatTypeDisplayName(code: string): string {
  return statTypeDisplayMap[code] || code;
}
